import { useMemo } from 'react';

import { useAssetQuery } from '../queries/assets';
import { useAppSelector } from '../store/hooks';
import { MetadataOptionsQuestion } from '../types/questions';

export interface AssetCurrentLocation {
  id: number;
  name: string;
  code?: string;
}

/**
 * Returns the current (last) nestable location for the asset selected in the associated
 * "assets" question, when this question is a location-update field (associatedMetadata.updateLocation).
 * Used for select_metadata with metadata_code nestable_locations or locations.
 */
export function useAssetCurrentLocation(
  question: MetadataOptionsQuestion,
): AssetCurrentLocation | null {
  const questions = useAppSelector((state) => state.webForms.questions);
  const answers = useAppSelector((state) => state.webForms.answers);
  const metadataMap = useAppSelector((state) => state.webForms.metadata);

  const selectedAssetId = useMemo(() => {
    const { questionId, updateLocation } = question.associatedMetadata ?? {};

    const assetsQuestion = questions.find(
      (q): q is MetadataOptionsQuestion =>
        q.type === 'select_metadata' && q.id === String(questionId) && q.metadata_code === 'assets',
    );

    const assetAnswer = answers[assetsQuestion?.id]?.value;
    const selectedAsset = metadataMap['assets']?.find((asset) => asset.name === assetAnswer);

    if (
      updateLocation === '1' &&
      assetsQuestion &&
      typeof assetAnswer === 'string' &&
      assetAnswer &&
      selectedAsset
    ) {
      return selectedAsset.id;
    }

    return null;
  }, [question, questions, answers, metadataMap]);

  const { data: assetData } = useAssetQuery(selectedAssetId);

  const nestableLocation = assetData?.asset?.nestable_location;
  if (!nestableLocation || typeof nestableLocation !== 'object') {
    return null;
  }
  const location = nestableLocation as { id?: number; name?: string; code?: string };
  if (location.id == null || location.name == null) return null;
  return {
    id: location.id,
    name: location.name,
    code: location.code,
  };
}
